import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  FormControl as MUIFormControl,
  InputLabel,
  Grid,
  Button,
  SelectChangeEvent,
  TextField,
  ButtonGroup,
  Stack,
  Typography,
} from "@mui/material"

export interface QuickSetupData {
  setupType: string
  anteriorSpace: string
  overbite: string
  midline: string
}

interface QuickSetupDlgProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: QuickSetupData) => void
  initialData?: Partial<QuickSetupData> // Optional initial data
}

const setupTypeOptions = ["Full", "Full no AP", "Anterior", "Alignment"]
const anteriorSpaceOptions = ["Improve", "0.5mm", "1.0mm"]
// const overbiteOptions = ["Improve", "1mm", "2mm", "3mm"] // Old options
const overbitePresetValues = ["Improve", "1", "2", "3"]
const overbitePresetLabels: Record<string, string> = {
  // Added type for clarity
  Improve: "Improve",
  "1": "1mm",
  "2": "2mm",
  "3": "3mm",
}
const midlineOptions = ["Improve", "to Upper", "to Lower"]

export const QuickSetupDlg: React.FC<QuickSetupDlgProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
}) => {
  const [setupType, setSetupType] = useState<string>(
    initialData?.setupType || setupTypeOptions[0],
  )
  const [anteriorSpace, setAnteriorSpace] = useState<string>(
    initialData?.anteriorSpace || anteriorSpaceOptions[0],
  )
  const [overbite, setOverbite] = useState<string>(
    initialData?.overbite || overbitePresetValues[0],
  )
  const [midline, setMidline] = useState<string>(
    initialData?.midline || midlineOptions[0],
  )

  useEffect(() => {
    if (open && initialData) {
      setSetupType(initialData.setupType || setupTypeOptions[0])
      setAnteriorSpace(initialData.anteriorSpace || anteriorSpaceOptions[0])
      setOverbite(initialData.overbite || overbitePresetValues[0])
      setMidline(initialData.midline || midlineOptions[0])
    } else if (open) {
      // Reset to defaults if no initial data when opening
      setSetupType(setupTypeOptions[0])
      setAnteriorSpace(anteriorSpaceOptions[0])
      setOverbite(overbitePresetValues[0]) // Default to "Improve"
      setMidline(midlineOptions[0])
    }
  }, [open, initialData])

  const handleSubmit = () => {
    onSubmit({
      setupType,
      anteriorSpace,
      overbite,
      midline,
    })
    onClose() // Typically, you'd close the dialog on submit
  }

  const isOverbiteNumeric = (value: string) =>
    !isNaN(parseFloat(value)) && isFinite(Number(value))

  const handleOverbiteNumericChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const val = event.target.value
    // Allow empty input temporarily, or valid numbers (even partial like "1.")
    if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
      setOverbite(val)
    }
  }

  const handleOverbiteBlur = () => {
    if (overbite === "Improve") return

    if (isOverbiteNumeric(overbite)) {
      // Normalize: "1." -> "1", "1.0" -> "1", ensure it's a string
      setOverbite(parseFloat(overbite).toString())
    } else {
      // If it's empty or non-numeric after typing, revert to a default.
      setOverbite(overbitePresetValues[0]) // Default to "Improve"
    }
  }

  const handleOverbitePresetClick = (presetValue: string) => {
    setOverbite(presetValue)
    if (presetValue !== "Improve" && !isOverbiteNumeric(presetValue)) {
      // This case should not happen if presetValues are correct
      console.warn("Invalid numeric preset for overbite:", presetValue)
    }
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Quick Setup Configuration</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3} sx={{ paddingTop: 2 }}>
          <Grid item xs={12}>
            <MUIFormControl fullWidth>
              <InputLabel id="setup-type-label">Setup Type</InputLabel>
              <Select
                labelId="setup-type-label"
                value={setupType}
                label="Setup Type"
                onChange={(e: SelectChangeEvent<string>) =>
                  setSetupType(e.target.value)
                }
              >
                {setupTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </MUIFormControl>
          </Grid>

          <Grid item xs={12}>
            <MUIFormControl fullWidth>
              <InputLabel id="anterior-space-label">Anterior Space</InputLabel>
              <Select
                labelId="anterior-space-label"
                value={anteriorSpace}
                label="Anterior Space"
                onChange={(e: SelectChangeEvent<string>) =>
                  setAnteriorSpace(e.target.value)
                }
              >
                {anteriorSpaceOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </MUIFormControl>
          </Grid>

          <Grid item xs={12}>
            <MUIFormControl fullWidth component="fieldset">
              {/* Using Typography as a fieldset legend alternative for simple title */}
              <Typography
                variant="caption"
                component="legend"
                sx={{
                  mb: 0.5,
                  fontSize: "0.75rem",
                  color: "rgba(0, 0, 0, 0.6)",
                }}
              >
                Overbite
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  type="number"
                  value={
                    overbite === "Improve" || !isOverbiteNumeric(overbite)
                      ? ""
                      : overbite
                  }
                  onChange={handleOverbiteNumericChange}
                  onBlur={handleOverbiteBlur}
                  placeholder={overbite === "Improve" ? "Auto" : "mm"}
                  disabled={overbite === "Improve"}
                  inputProps={{
                    step: "0.1",
                    // style: { MozAppearance: "textfield" }, // Removed to show Firefox spinners
                  }} // step for arrows
                  sx={{
                    width: "120px",
                    // Removed sx that hides Webkit spinners
                  }}
                  size="small"
                />
                <ButtonGroup size="small">
                  {overbitePresetValues.map((value) => (
                    <Button
                      key={value}
                      onClick={() => handleOverbitePresetClick(value)}
                      variant={overbite === value ? "contained" : "outlined"}
                    >
                      {overbitePresetLabels[value]}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </MUIFormControl>
          </Grid>

          <Grid item xs={12}>
            <MUIFormControl fullWidth>
              <InputLabel id="midline-label">Midline</InputLabel>
              <Select
                labelId="midline-label"
                value={midline}
                label="Midline"
                onChange={(e: SelectChangeEvent<string>) =>
                  setMidline(e.target.value)
                }
              >
                {midlineOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </MUIFormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained">
          Apply Setup
        </Button>
      </DialogActions>
    </Dialog>
  )
}
